// src/components/Navigation/Navigation.tsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { siteConfig, getAvailableCategories } from '../../config/siteConfig';
import './Navigation.css';

interface NavigationProps {
  transparent?: boolean;
}

const Navigation: React.FC<NavigationProps> = ({ transparent = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [dropdownTimeout, setDropdownTimeout] = useState<NodeJS.Timeout | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const categories = getAvailableCategories();
  const navCategories = categories.map(cat => ({
    id: cat.id,
    label: cat.title,
    icon: cat.icon,
    items: cat.subcategories.map(sub => ({
      path: sub.path,
      label: sub.title,
      count: sub.count,
      status: sub.status
    })),
    status: cat.status
  }));

  const socialLinks = [];
  
  if (siteConfig.author.social.youtube) {
    socialLinks.push({ 
      name: 'YouTube', 
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="#FF0000">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      ), 
      url: siteConfig.author.social.youtube
    });
  }
  
  if (siteConfig.author.social.twitter) {
    socialLinks.push({ 
      name: 'X', 
      icon: '𝕏', 
      url: siteConfig.author.social.twitter
    });
  }

  const handleDropdownToggle = (categoryId: string) => {
    setActiveDropdown(activeDropdown === categoryId ? null : categoryId);
  };

  const handleMouseEnter = (categoryId: string) => {
    if (dropdownTimeout) {
      clearTimeout(dropdownTimeout);
      setDropdownTimeout(null);
    }
    setActiveDropdown(categoryId);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setActiveDropdown(null);
    }, 300); // Délai de 300ms avant de fermer
    setDropdownTimeout(timeout);
  };

  return (
    <>
      <nav className={`modern-nav ${isScrolled || !transparent ? 'scrolled' : ''} ${transparent ? 'transparent' : ''}`}>
        <div className="nav-container">
          {/* Logo */}
          <Link to="/" className="nav-brand">
            <img 
              src="/images/logoP&F.jpg" 
              alt="Logo" 
              className="nav-logo-img"
            />
            <div className="nav-brand-text">
              <span className="brand-name">{siteConfig.siteName}</span>
              <span className="brand-tagline">{siteConfig.siteNameAr}</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="nav-center">
            <Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}>
              <span>Accueil</span>
            </Link>
            
            {navCategories.map((category) => (
              <div 
                key={category.id} 
                className="nav-dropdown"
                onMouseEnter={() => category.items.length > 0 && handleMouseEnter(category.id)}
                onMouseLeave={handleMouseLeave}
              >
                <button 
                  className={`nav-link dropdown-toggle ${category.status === 'soon' ? 'disabled' : ''}`}
                  onClick={() => handleDropdownToggle(category.id)}
                >
                  <span>{category.label}</span>
                  {category.status === 'soon' && <span className="badge-soon">Bientôt</span>}
                  {category.items.length > 0 && (
                    <svg className="dropdown-arrow" width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <a
                href="https://zakat-calculateur.netlify.app/"
                className="mobile-social-btn zakat-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img 
                  src="/images/logoZakat.png" 
                  alt="Zakat" 
                  style={{ width: '24px', height: '24px', borderRadius: '4px' }}
                />
                <span>Zakat Calculateur</span>
              </a>
                      <path d="M7 10l5 5 5-5z"/>
                    </svg>
                  )}
                </button>
                
                {category.items.length > 0 && activeDropdown === category.id && (
                  <div className="dropdown-menu">
                    {category.items.map((item) => (
                      <Link 
                        key={item.path} 
                        to={item.path} 
                        className={`dropdown-item ${item.status === 'soon' ? 'disabled' : ''}`}
                      >
                        <span className="item-label">{item.label}</span>
                        <div className="item-meta">
                          {item.count && <span className="item-count">{item.count}</span>}
                          {'status' in item && item.status === 'soon' && <span className="badge-soon">Bientôt</span>}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Social Links */}
          <div className="nav-social">
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.url}
                className="social-btn"
                target="_blank"
                rel="noopener noreferrer"
                title={social.name}
              >
                {typeof social.icon === 'string' ? social.icon : social.icon}
              </a>
            ))}
            <a
              href="https://zakat-calculateur.netlify.app/"
              className="social-btn zakat-btn-nav"
              target="_blank"
              rel="noopener noreferrer"
              title="Calculateur de Zakat"
            >
              <img 
                src="/images/logoZakat.png" 
                alt="Zakat" 
                className="zakat-logo-nav"
              />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`mobile-toggle ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className="hamburger">
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div className={`mobile-overlay ${isMobileMenuOpen ? 'active' : ''}`} onClick={() => setIsMobileMenuOpen(false)} />
      
      {/* Mobile Menu */}
      <div className={`mobile-menu ${isMobileMenuOpen ? 'active' : ''}`}>
        <div className="mobile-menu-header">
          <img 
            src="/images/logoP&F.jpg" 
            alt="Logo" 
            className="mobile-logo"
          />
          <button 
            className="mobile-close"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            ✕
          </button>
        </div>
        
        <div className="mobile-menu-content">
          <Link to="/" className="mobile-link">
            <span className="nav-icon">🏠</span>
            <span>Accueil</span>
          </Link>
          
          {navCategories.map((category) => (
            <div key={category.id} className="mobile-category">
              <div className={`mobile-category-header ${category.status === 'soon' ? 'disabled' : ''}`}>
                <span className="nav-icon">{category.icon}</span>
                <span>{category.label}</span>
                {category.status === 'soon' && <span className="badge-soon">Bientôt</span>}
              </div>
              
              {category.items.length > 0 && (
                <div className="mobile-submenu">
                  {category.items.map((item) => (
                    <Link 
                      key={item.path} 
                      to={item.path} 
                      className={`mobile-sublink ${item.status === 'soon' ? 'disabled' : ''}`}
                    >
                      <span>{item.label}</span>
                      {item.count && <span className="item-count">{item.count}</span>}
                      {'status' in item && item.status === 'soon' && <span className="badge-soon">Bientôt</span>}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        
        <div className="mobile-menu-footer">
          <div className="mobile-social">
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.url}
                className="mobile-social-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                {typeof social.icon === 'string' ? social.icon : social.icon}
                <span>{social.name}</span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
